import * as React from "react";
import Image from "next/image";
import axios from "axios";
import { motion } from "framer-motion";

import Layout from "@/components/layout";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/cn";
import GoldEnrollNow from "@/components/pages/paid/GoldEnrollNow";
import webengage from "@/webengage";
import mixpanel from "@/utils/mixpanel";
import Breadcrumb from "@/components/BreadCrumb";
import Head from "next/head";

export const faqs = [
  {
    question: "What is Tradewise Gold?",
    answer:
      "Tradewise Gold is a premium subscription service providing advanced trading tools and educational resources. It includes access to technical and fundamental screeners, recorded lectures, trading indicators, eBooks, a Trading helpline, unlimited access to past recordings, and a 10% discount on other Tradewise courses.",
  },
  {
    question: "Who can benefit from subscribing to Tradewise Gold?",
    answer:
      "Traders and investors at all levels who seek comprehensive support, cutting-edge tools, and continuous education in their trading journey will find Tradewise Gold highly beneficial.",
  },
  {
    question:
      "What types of educational materials are included in Tradewise Gold?",
    answer: `Tradewise Gold includes a range of educational resources such as recorded lectures on trading and investing topics, eBooks on market strategies, and indicators to assist with analysis. You can access these materials through the platform’s "Resources" or "Lectures" section.`,
  },
  {
    question: "What kind of support is available through the Trading helpline?",
    answer: `The Trading helpline connects you with experienced traders and analysts who offer real-time advice and assistance with your trading queries and strategies. You can reach this service through the "Support" or "Helpline" section or by calling the designated number during trading hours.`,
  },
  {
    question: "What does the 10% discount on other Tradewise courses include?",
    answer:
      "As a Tradewise Gold subscriber, you receive a 10% discount on any additional courses offered by Tradewise. This discount is automatically applied at checkout when you purchase other courses.",
  },
];

export const benefits = {
  goldBenefits: [
    {
      header: "Screeners",
      points: [
        {
          title: "Technical Screeners",
          description: "Advanced tools to analyze market trends and signals.",
          price: "2,000",
          icon: "technical-screeners.svg",
        },
        {
          title: "Fundamental Screeners",
          description:
            "In-depth analysis of company performance and market conditions.",
          price: "1,000",
          icon: "fundamental-screeners.svg",
        },
      ],
    },
    {
      header: "Prerequisities of Trading",
      points: [
        {
          title: "Recorded Lectures",
          description:
            "Comprehensive video tutorials covering essential trading knowledge.",
          price: "1,000",
          icon: "recorded-lectures.svg",
        },
      ],
    },
    {
      header: "Indicators",
      points: [
        {
          title: "Indicators",
          description:
            "Access to a curated list of powerful trading indicators to enhance your strategies.",
          price: "3,500",
          icon: "indicators.svg",
        },
      ],
    },
    {
      header: "E-books",
      points: [
        {
          title: "E-books",
          description:
            "A library of insightful e-books coveringvarious trading topics and strategies.",
          price: "2,500",
          icon: "e-books.svg",
        },
      ],
    },
    {
      header: "Trading Helpline",
      points: [
        {
          title: "Trading Helpline",
          description:
            "Get real-time assistance from our expert trading support team.",
          price: "3,000",
          icon: "live-trading-helpline.svg",
        },
      ],
    },
    {
      header: "Brand Collaborations",
      points: [
        {
          title: "Brand Collaborations",
          description:
            "Benefit from exclusive offers and insights through our strategic brand partnership.",
          price: "15,000",
          icon: "brand-collaboration.svg",
        },
      ],
    },
  ],
  otherBenefits: [
    {
      header: "Lifetime Access",
      points: [
        {
          title: "Unlimited Recording Access",
          description:
            "Enjoy lifetime access to recordings of all the courses you purchase.",

          icon: "lifetime-access.svg",
        },
      ],
    },
    {
      header: "Exclusive Discounts",
      points: [
        {
          title: "Extra 10% Discount",
          description: "Enjoy a 10% discount on all Tradewise courses.",
          icon: "exclusive-discounts.svg",
        },
      ],
    },
  ],
};

type Inputs = {
  name: string;
  email: string;
  phone: string;
};

const PAYMENT_STATUS = {
  COMPLETED: "COMPLETED",
  PENDING: "PENDING",
  FAILED: "FAILED",
};

export default function Gold() {
  const [activeFAQ, setActiveFAQ] = React.useState<number[]>([]);
  const [isCTAVisible, setIsCTAVisible] = React.useState(true);
  const ctaRef = React.useRef<HTMLButtonElement>(null);
  const contentRef = React.useRef<(HTMLDivElement | null)[]>([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [isGoldVisible, setIsGoldVisible] = React.useState(false);

  const toggleFAQ = (index: number) => {
    setActiveFAQ((prev) => {
      if (prev.includes(index)) {
        return prev.filter((item) => item !== index);
      }

      return [...prev, index];
    });
  };

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsCTAVisible(true);
          } else {
            setIsCTAVisible(false);
          }
        });
      },
      { threshold: 0.3 },
    );

    if (ctaRef.current) {
      observer.observe(ctaRef.current);
    }

    return () => {
      if (ctaRef.current) {
        observer.unobserve(ctaRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const _webengage = webengage();
    if (_webengage) {
      _webengage.track("Gold plan page viewed", {
        source: "Tradewise",
      });
    }
  }, []);

  if (!isGoldVisible) {
    return <></>;
  }

  return (
    <>
      <Head>
        <title>
          Tradewise Gold Membership| Master Trading Strategies with Tradewise
        </title>
        <meta
          name="description"
          content="Unlock exclusive gold membership at Tradewise. Get premium access to expert trading strategies, personalized support, and in-depth market insights."
        />
      </Head>
      <GoldEnrollNow
        _data={{
          type: "Tradewise",
          price: 28000,
          discountedPrice: 999,
          duration: "3 MONTHS",
        }}
        isOpen={modalOpen}
        source="tradewise"
        onClose={() => setModalOpen(false)}
      />
      <Image
        src="/gold/waves.svg"
        width={428}
        height={2056}
        alt="wavy background"
        className="fixed max-md:inset-0 md:top-0 md:left-0 -translate-y-16 opacity-25"
      />
      <Image
        src="/gold/waves2.svg"
        width={662}
        height={1870}
        alt="wavy background"
        className="max-md:hidden fixed top-0 right-0 opacity-25"
      />
      <Layout
        className="bg-cover !pb-16 sm:!pb-14 md:!pb-12 font-inter"
        gold
        hideFooter
      >
        <section className="relative py-4 max-md:pt-8">
          <Breadcrumb classNames="absolute top-0 left-0 bg-white px-2 py-1 rounded-lg z-30" />
          <Image
            src="/gold/logo.svg"
            alt="TradeWise Gold"
            width={190}
            height={190}
            className="mx-auto"
            loading="eager"
            quality={100}
          />
          <p className="max-sm:text-sm tracking-wide text-center mt-1.5 sm:mt-2 md:mt-3">
            Elevate your trading journey
          </p>
          <div className="mt-6">
            <div className="rounded-xl border-2 border-[#FFD691] bg-gradient-to-b from-white to-[#FFF7EA] p-5">
              <div className="flex items-center justify-center gap-1.5 mt-0.5">
                <Image src="/gold/dash.svg" alt="Dash" width={60} height={60} />
                <h4 className="uppercase whitespace-nowrap text-sm text-[#694A07] font-bold">
                  Special Price for you
                </h4>
                <Image
                  src="/gold/dash.svg"
                  alt="Dash"
                  width={60}
                  height={60}
                  className="rotate-180"
                />
              </div>
              <div className="md:flex justify-evenly items-center md:mx-8">
                <div className="px-1 space-y-2.5">
                  <div className="flex items-center justify-center gap-x-5 mt-3 md:mb-3">
                    <p className="bg-gradient-to-r from-[#9A7026] via-[#D8AB65] to-[#9A7026] bg-clip-text text-transparent text-3xl tracking-[0] whitespace-nowrap font-bold md:text-5xl">
                      ₹999
                    </p>
                    <div>
                      <p className="relative w-fit font-semibold">
                        <span className="h-[1.5px] rounded-md bg-black absolute top-0 left-0 w-16 -rotate-[10deg] translate-y-2.5 md:text-xl" />
                        ₹28,000
                      </p>
                      <p className="text-zinc-700 text-xs md:text-sm md:w-max">
                        For 3 months
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  ref={ctaRef}
                  className="bg-black hover:bg-black/95 w-full h-[52px] text-3xl mt-[18px] mb-3.5 md:w-max md:px-28"
                  onClick={() => {
                    setModalOpen(true);
                    mixpanel.track("join_gold", {
                      source: "Tradewise",
                    });
                    const _webengage = webengage();
                    if (_webengage) {
                      _webengage.track("Gold plan purchase initiated", {
                        type: "Tradewise",
                        price: 28000,
                        discountedPrice: 999,
                        duration: "3 MONTHS",
                      });
                    }
                  }}
                >
                  <span className="bg-gradient-to-r from-[#eec883] via-[rgb(255,234,196)] to-[#eec883] bg-clip-text font-bold text-transparent text-xl text-center tracking-[0] leading-[22.8px] whitespace-nowrap">
                    Join Gold
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="relative py-3 mt-3">
          <div className="flex items-center justify-center gap-2.5">
            <Image src="/gold/star.svg" alt="Star" width={15} height={15} />
            <h3 className="uppercase text-base font-semibold tracking-[6px]">
              Gold Benefits
            </h3>
            <Image src="/gold/star.svg" alt="Star" width={15} height={15} />
          </div>
          <div className="mt-1 md:mt-4 grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {benefits.goldBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.header}
                initial={{
                  opacity: 0,
                  translateY: 40,
                }}
                whileInView={{
                  opacity: 1,
                  translateY: 0,
                }}
                transition={{ duration: 0.4, type: "tween" }}
                viewport={{ once: true }}
                className={cn(
                  "w-full rounded-xl border-2 border-[#FFD691] bg-gradient-to-b from-white to-[#FFF7EA] p-6 pb-9",
                  index === 0 || index === 5
                    ? "col-span-1 md:col-span-2"
                    : "col-span-1",
                )}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <Image
                    src="/gold/dash.svg"
                    alt="Dash"
                    width={45}
                    height={45}
                  />
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 relative">
                      <Image src="/gold/bars.svg" alt="Bars Icon" fill />
                    </div>
                    <h4 className="text-[#694A07] font-bold whitespace-nowrap">
                      {benefit.header}
                    </h4>
                  </div>
                  <Image
                    src="/gold/dash.svg"
                    alt="Dash"
                    width={45}
                    height={45}
                    className="rotate-180"
                  />
                </div>
                <div
                  className={cn(
                    "gap-y-[30px] gap-x-[78px] grid grid-cols-1 mt-3.5",
                    index === 0 && "md:grid-cols-2",
                    index === 5 ? "md:w-fit md:mx-auto" : "mx-0",
                  )}
                >
                  {benefit.points.map((point) => (
                    <div key={point.title} className="flex items-center gap-5">
                      <div className="relative h-[40px] w-[40px]">
                        <Image
                          src={`/gold/benefits/${point.icon}`}
                          alt={point.title}
                          fill
                          className="object-cover"
                        />
                      </div>
                      <div className={`space-y-1 flex-1 `}>
                        <div className="flex items-center justify-between">
                          <h5 className="font-semibold lg:w-[200px]">
                            {point.title}
                          </h5>
                          <p className="bg-gradient-to-r from-[#9A7026] via-[#D8AB65] to-[#9A7026] bg-clip-text text-transparent tracking-[0] whitespace-nowrap font-bold">
                            ₹{point.price}
                          </p>
                        </div>
                        <p className="text-[15px] leading-snug text-zinc-700">
                          {point.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Other Benefits */}
          <div className="flex items-center justify-center gap-2.5 mt-10">
            <Image src="/gold/star.svg" alt="Star" width={15} height={15} />
            <h3 className="uppercase text-base font-semibold tracking-[6px]">
              Other Benefits
            </h3>
            <Image src="/gold/star.svg" alt="Star" width={15} height={15} />
          </div>
          <div className="mt-1 md:mt-4 grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {benefits.otherBenefits.map((benefit) => (
              <motion.div
                key={benefit.header}
                initial={{
                  opacity: 0,
                  translateY: 40,
                }}
                whileInView={{
                  opacity: 1,
                  translateY: 0,
                }}
                transition={{ duration: 0.4, type: "tween" }}
                viewport={{ once: true }}
                className="w-full col-span-1 rounded-xl border-2 border-[#FFD691] bg-gradient-to-b from-white to-[#FFF7EA] p-6 pb-9"
              >
                <div className="flex items-center justify-center gap-1.5">
                  <Image
                    src="/gold/dash.svg"
                    alt="Dash"
                    width={45}
                    height={45}
                  />
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 relative">
                      <Image src="/gold/bars.svg" alt="Bars Icon" fill />
                    </div>
                    <h4 className="text-[#694A07] font-bold whitespace-nowrap">
                      {benefit.header}
                    </h4>
                  </div>
                  <Image
                    src="/gold/dash.svg"
                    alt="Dash"
                    width={45}
                    height={45}
                    className="rotate-180"
                  />
                </div>
                <div className="space-y-[30px] mt-3.5">
                  {benefit.points.map((point) => (
                    <div key={point.title} className="flex items-center gap-5">
                      <div className="relative h-[40px] w-[40px]">
                        <Image
                          src={`/gold/benefits/${point.icon}`}
                          alt={point.title}
                          fill
                          className="object-cover"
                        />
                      </div>
                      <div className="space-y-1 flex-1">
                        <h5 className="font-semibold">{point.title}</h5>
                        <p className="text-[15px] leading-snug text-zinc-700">
                          {point.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section className="py-3 mt-4 relative md:pb-12">
          <div className="flex items-center justify-center gap-2.5">
            <Image src="/gold/star.svg" alt="Star" width={15} height={15} />
            <h3 className="font-semibold text-base tracking-[6px]">FAQs</h3>
            <Image src="/gold/star.svg" alt="Star" width={15} height={15} />
          </div>
          <motion.div
            initial={{
              opacity: 0,
              translateY: 40,
            }}
            whileInView={{
              opacity: 1,
              translateY: 0,
            }}
            transition={{ duration: 0.4, type: "tween" }}
            viewport={{ once: true }}
            className="rounded-xl mt-1 md:mt-4 border-2 border-[#FFD691] bg-gradient-to-br from-white to-[#FFF7EA]"
          >
            {faqs.map((faq, index) => {
              const isActive = activeFAQ.includes(index);
              return (
                <div
                  className={cn(
                    "p-[18px] cursor-pointer overflow-hidden transition-all duration-200 ease-linear",
                    index !== faqs.length - 1 &&
                      "border-b-2 border-b-[#FFD691]",
                  )}
                  onClick={() => {
                    toggleFAQ(index);
                    mixpanel.track("faq_toggled", {
                      question: faq.question,
                      location: "goldPage",
                    });
                  }}
                  key={index}
                >
                  <div className="flex justify-between items-center">
                    <p className="w-4/5 font-semibold leading-tight tracking-wide">
                      {faq.question}
                    </p>
                    <ChevronDown
                      size={30}
                      className={cn(
                        "transition-transform duration-200 ease-linear",
                        isActive ? "rotate-180" : "rotate-0",
                      )}
                    />
                  </div>
                  <div
                    ref={(el) => (contentRef.current[index] = el)}
                    className="overflow-hidden transition-max-height duration-200 ease-linear"
                    style={{
                      maxHeight: isActive
                        ? `${contentRef.current[index]?.scrollHeight}px`
                        : "0px",
                    }}
                  >
                    <p
                      data-state={isActive ? "open" : "closed"}
                      className="mt-1 w-4/5 text-[15px] leading-snug text-zinc-700 data-[state=open]:opacity-100 data-[state=closed]:opacity-0 transition-opacity duration-300 ease-linear"
                    >
                      {faq.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </section>

        {/* Fixed CTA */}
        <section
          className={cn(
            "fixed z-10 inset-x-0 bottom-0 px-4 sm:px-6 md:px-10 lg:px-16 pb-2.5 md:pt-2.5 w-full md:flex justify-between transition-all duration-200 ease-out bg-gradient-to-t from-[#fdf5ea] from-75% to-transparent container",
            isCTAVisible ? "translate-y-full" : "translate-y-0",
          )}
        >
          <div className="hidden md:flex items-center justify-center gap-x-5 mt-3">
            <p className="bg-gradient-to-r from-[#9A7026] via-[#D8AB65] to-[#9A7026] bg-clip-text text-transparent text-3xl tracking-[0] whitespace-nowrap font-bold">
              ₹999
            </p>
            <div>
              <p className="relative w-fit font-semibold">
                <span className="h-[1.5px] rounded-md bg-black absolute top-0 left-0 w-16 -rotate-[10deg] translate-y-2.5" />
                ₹28,000
              </p>
              <p className="text-zinc-700 text-xs md:w-max">For 3 months</p>
            </div>
          </div>
          <Button
            className="bg-black hover:bg-black/95 w-full h-[52px] text-3xl md:w-max md:px-28"
            onClick={() => {
              setModalOpen(true);
              const _webengage = webengage();
              if (_webengage) {
                _webengage.track("Gold plan purchase initiated", {
                  type: "Tradewise",
                  price: 28000,
                  discountedPrice: 999,
                  duration: "3 MONTHS",
                });
              }
            }}
          >
            <span className="bg-gradient-to-r from-[#eec883] via-[rgb(255,234,196)] to-[#eec883] bg-clip-text font-bold text-transparent text-xl text-center tracking-[0] leading-[22.8px] whitespace-nowrap">
              Join Gold
            </span>
          </Button>
        </section>
      </Layout>
    </>
  );
}
